import React, { useContext, useEffect, useRef, useState } from "react";
import { Page_settings } from "@/config/page_settings";
import { styled, keyframes } from "styled-components";

import fonts from "@/design/typography";
import colors from "@/design/colors";
import IconX from "@/components/icons/x";
import Button from "@/components/button";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";
import RowHeaderTable from "@/components/table/RowHeaderTable";
import { businessTypeCode } from "@/type/payments/businessTypeCode";
import { deviceWidth } from "@/design/defaultTheme";
import { useWindowSize } from "@/hooks/useWindowSize";

/* **********************************
 * TYPES
 ********************************** */
interface BusinessTypeCodeModalProps {
  onClose: () => void; // 모달 닫기
  onSelect: (code: string, name: string) => void; // 업태코드 선택 시 부모에게 전달
  selectedBusinessCode?: string; // 부모에서 넘겨줄 값
}

interface BusinessTypeCode {
  code: string;
  category: string;
  subcategory: string;
  selected: boolean;
}

const BusinessTypeCodeModal: React.FC<BusinessTypeCodeModalProps> = ({
  onClose,
  onSelect,
  selectedBusinessCode,
  
}) => {
  const { width } = useWindowSize();
  const isMobile = width < deviceWidth.mobile;

  /* **********************************
   * GLOBAL STATE
   ********************************** */
  const context: any = useContext(Page_settings);

  /* **********************************
   * LOCAL STATE
   ********************************** */
  const [selectedCode, setSelectedCode] = useState<string>("");
  const [selectedName, setSelectedName] = useState<string>("");

  // 업태코드 목록을 배열로 변환
  const businessTypeCodes: BusinessTypeCode[] = Object.entries(
    businessTypeCode
  ).flatMap(([category, items]) =>
    items.map((item) => ({
      code: item.code,
      category: category,
      subcategory: item.label,
      selected: false,
    }))
  );

  // 카테고리별로 그룹화
  const groupedBusinessTypeCodes = businessTypeCodes.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, BusinessTypeCode[]>);

  // 렌더링용 데이터 생성 (rowSpan 정보 포함)
  const renderData = Object.entries(groupedBusinessTypeCodes).flatMap(
    ([category, items]) =>
      items.map((item, index) => ({
        ...item,
        isFirstInCategory: index === 0,
        categoryRowSpan: items.length,
      }))
  );

  // 모달 추적 ref
  const modalRef = useRef<HTMLDivElement>(null);

  /* **********************************
   * Handlers
   ********************************** */
  const handleRowClick = (
    code: string,
    category: string,
    subcategory: string
  ) => {
    setSelectedName(`${category} > ${subcategory} > ${code}`);
    setSelectedCode(code);
  };

  const handleComplete = () => {
    if (selectedCode && selectedName) {
      onSelect(selectedCode, selectedName);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
      
    setSelectedCode(selectedBusinessCode);
      
  }, []);

  

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalWrap ref={modalRef} $isMobile={isMobile}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: isMobile ? "24px" : "32px",
          }}
        >
          <Title $isMobile={isMobile}>업태코드 선택</Title>
          <div onClick={onClose}>
            <HoverableIcon>
              <IconX color="#333" width="24px" height="24px" />
            </HoverableIcon>
          </div>
        </div>

        <TableScrollContainer $isMobile={isMobile}>
          <CustomTable>
            <TableHeader>
              <TableRow>
                <HeaderCell $isMobile={isMobile}>대분류</HeaderCell>
                <HeaderCell $isMobile={isMobile}>소분류</HeaderCell>
                <HeaderCell $isMobile={isMobile}>업태코드</HeaderCell>
                <HeaderCell $isMobile={isMobile}>선택</HeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderData.map((item, index) => (
                <TableRow
                  className="hover-row"
                  key={index}
                  onClick={() =>
                    handleRowClick(item.code, item.category, item.subcategory)
                  }
                >
                  {item.isFirstInCategory ? (
                    <TableCell
                      $isMobile={isMobile}
                      rowSpan={item.categoryRowSpan}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.category}
                    </TableCell>
                  ) : null}
                  <TableCell $hoverable $isMobile={isMobile}>
                    {item.subcategory}
                  </TableCell>
                  <TableCell $hoverable $isMobile={isMobile}>
                    {item.code}
                  </TableCell>
                  <TableCell $hoverable $isMobile={isMobile}>
                    <RadioButton
                      selected={selectedCode === item.code}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(
                          item.code,
                          item.category,
                          item.subcategory
                        );
                      }}
                    >
                      {selectedCode === item.code && <RadioButtonInner />}
                    </RadioButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </CustomTable>
        </TableScrollContainer>

        <Button
          onClick={handleComplete}
          size="large"
          text={"완료"}
          height="43px"
          width="100%"
          disabled={!selectedCode}
          buttonColor={selectedCode ? colors.blue600 : colors.gray400}
        />
      </ModalWrap>
    </Overlay>
  );
};

/* **********************************
 * Styles
 ********************************** */
const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrap = styled.div<{ $isMobile?: boolean }>`
  width: ${({ $isMobile }) => ($isMobile ? "calc(100% - 40px)" : "720px")};
  height: fit-content;
  height: 80vh;
  border-radius: ${({ $isMobile }) => ($isMobile ? "12px" : "20px")};
  background-color: ${colors.white};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: ${({ $isMobile }) => ($isMobile ? "16px" : "20px 24px")};
  overflow: hidden;
`;

const Title = styled.div<{ $isMobile?: boolean }>`
  font-size: ${({ $isMobile }) =>
    $isMobile ? "14px" : fonts.body2SemiBold.fontSize};
  line-height: ${({ $isMobile }) =>
    $isMobile ? "20px" : fonts.body2SemiBold.lineHeight};
  font-family: ${fonts.body2SemiBold.fontFamily};
  color: ${colors.gray900};
`;

const HoverableIcon = styled.div`
  display: inline-flex;
  cursor: pointer;
`;

const CustomTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${colors.gray100};
`;

const HeaderCell = styled.th<{ $isMobile?: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "8px" : "12px")};
  text-align: center;
  font-size: ${({ $isMobile }) =>
    $isMobile ? "11px" : fonts.label4Medium.fontSize};
  font-family: ${fonts.label4Medium.fontFamily};
  color: ${colors.gray700};
  border-right: 1px solid ${colors.gray200};
  border-bottom: 1px solid ${colors.gray200};

  &:nth-child(1) {
    width: ${({ $isMobile }) => ($isMobile ? "120px" : "160px")};
  }
  &:nth-child(2) {
    flex: 1;
  }
  &:nth-child(3) {
    width: ${({ $isMobile }) => ($isMobile ? "90px" : "120px")};
  }
  &:nth-child(4) {
    width: ${({ $isMobile }) => ($isMobile ? "60px" : "80px")};
    border-right: none;
  }
`;

const TableBody = styled.tbody`
  & tr {
    &:nth-child(even) {
      background-color: ${colors.gray50};
    }
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
`;

const TableCell = styled.td<{ $hoverable?: boolean; $isMobile?: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "8px" : "12px")};
  text-align: center;
  border-right: 1px solid ${colors.gray200};
  border-bottom: 1px solid ${colors.gray200};
  font-size: ${({ $isMobile }) =>
    $isMobile ? "11px" : fonts.label4Medium.fontSize};
  font-family: ${fonts.label4Medium.fontFamily};
  color: ${colors.gray900};
  transition: background-color 0.2s ease;

  &:nth-child(1) {
    width: ${({ $isMobile }) => ($isMobile ? "120px" : "160px")};
  }

  ${({ $hoverable }) =>
    $hoverable &&
    `
    .hover-row:hover & {
      background-color: ${colors.blue50};
    }
  `}
`;

const RadioButton = styled.div<{ selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ selected }) => (selected ? colors.blue600 : colors.gray300)};
  background-color: ${({ selected }) =>
    selected ? colors.blue600 : "transparent"};
  cursor: pointer;
  position: relative;
  margin: 0 auto;
`;

const RadioButtonInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${colors.white};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const TableScrollContainer = styled.div<{ $isMobile?: boolean }>`
  height: calc(80vh - ${({ $isMobile }) => ($isMobile ? "140px" : "170px")});
  overflow-y: auto;
  margin-bottom: ${({ $isMobile }) => ($isMobile ? "12px" : "20px")};
  border-radius: 8px;
  border: 1px solid ${colors.gray200};
`;

export default BusinessTypeCodeModal;
